import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import type { PickerUser } from '@/services/users.service';
import { SearchInput } from '@/components/ui/search-input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface Props {
  /** Selected owner ids. Empty means the goal is self-set. */
  value: string[];
  onChange: (ids: string[]) => void;
  people: PickerUser[];
  /** The signed-in user, pinned to the top as "Myself". */
  currentUserId: string;
  disabled?: boolean;
}

function label(u: PickerUser): string {
  const name = `${u.first_name} ${u.last_name}`.trim();
  return u.department_name ? `${name} · ${u.department_name}` : name;
}

/**
 * Multi-select owner picker for the goal form. Each person selected gets their own
 * independent goal, so this is a plain checklist rather than a shared-assignee list.
 *
 * Local to the goals module on purpose: `components/tasks/AssigneePicker` is a similar
 * control but belongs to Tasks, and importing across modules is banned. It's also a
 * different shape — always-open panel with avatars vs. this compact popover that fits
 * the form's two-column grid.
 */
export function GoalAssigneeSelect({ value, onChange, people, currentUserId, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close on outside click / Escape. Escape is stopped so it doesn't also close the
  // surrounding goal dialog.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    else setSearch('');
  }, [open]);

  const self = useMemo(() => people.find((p) => p.id === currentUserId), [people, currentUserId]);

  const others = useMemo(() => {
    const q = search.trim().toLowerCase();
    return people
      .filter((p) => p.id !== currentUserId)
      .filter((p) => (q ? label(p).toLowerCase().includes(q) : true));
  }, [people, currentUserId, search]);

  const selfMatchesSearch = useMemo(() => {
    const q = search.trim().toLowerCase();
    return !q || 'myself'.includes(q) || (self ? label(self).toLowerCase().includes(q) : false);
  }, [search, self]);

  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  }

  // Empty selection is meaningful (self-set), so the trigger says "Myself" rather than
  // looking like nothing has been chosen.
  const triggerText = (() => {
    if (value.length === 0) return 'Myself';
    if (value.length === 1) {
      const only = people.find((p) => p.id === value[0]);
      if (!only) return '1 employee';
      return only.id === currentUserId ? 'Myself' : `${only.first_name} ${only.last_name}`.trim();
    }
    return `${value.length} employees`;
  })();

  // Deliberately NOT a Radix Popover. This control lives inside a Radix Dialog, and the
  // popover's portalled content sits outside the dialog's focus trap — the search input
  // could be clicked but never kept focus, so typing did nothing. Rendering the panel
  // inline keeps it inside the dialog's own focus scope, so the input behaves normally.
  return (
    <div ref={rootRef} className="relative">
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        aria-label="Assign to"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="w-full justify-between font-normal"
      >
        <span className={cn('truncate', value.length === 0 && 'text-muted-foreground')}>
          {triggerText}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full overflow-hidden rounded-xl border border-border/60 bg-card shadow-lg">
          <div className="border-b border-border/60 p-2">
            <SearchInput
              ref={inputRef}
              value={search}
              onChange={setSearch}
              placeholder="Search employees…"
              containerClassName="w-full"
            />
          </div>

          <div className="max-h-60 overflow-y-auto overscroll-contain p-1">
            {selfMatchesSearch && (
              <Row
                selected={value.includes(currentUserId)}
                onClick={() => toggle(currentUserId)}
                text="Myself"
              />
            )}
            {others.map((p) => (
              <Row
                key={p.id}
                selected={value.includes(p.id)}
                onClick={() => toggle(p.id)}
                text={label(p)}
              />
            ))}
            {others.length === 0 && !selfMatchesSearch && (
              <p className="p-4 text-center text-sm text-muted-foreground">No employees found</p>
            )}
          </div>

          {value.length > 0 && (
            <div className="flex items-center justify-between gap-2 border-t border-border/60 px-3 py-2">
              <span className="text-xs text-muted-foreground">
                {value.length} selected — one goal each
              </span>
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-primary hover:underline"
                onClick={() => onChange([])}
              >
                <X className="h-3 w-3" /> Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ selected, onClick, text }: { selected: boolean; onClick: () => void; text: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors',
        selected ? 'bg-primary/10' : 'hover:bg-muted/50',
      )}
    >
      <span
        className={cn(
          'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
          selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
        )}
      >
        {selected && <Check className="h-3 w-3" />}
      </span>
      <span className="truncate">{text}</span>
    </button>
  );
}
